import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { Button, Drawer, message } from 'antd';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import { IPhoneListRsp, waBuyerComment } from 'services/api/waBuyers';
import WaConcatBtn from 'components/ContactWhatsapp/WaConcatBtn';
import InlineEditor from 'views/Setting/Shops/InlineEditor';
import { buyerNickMask } from 'services/channels/demo';
import { BlueTag, RedTag } from 'components/styled/tags';
import { CountryFlagIcon } from 'components/CountryFlagIcon';

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  &:before {
    content: '';
    height: 12px;
    width: 3px;
    background: #3370ff;
    display: inline-block;
    margin-right: 10px;
  }
  font-size: 15px;
  color: var(--fett-text-primary);
  margin-bottom: 8px;
  &:not(:first-child) {
    margin-top: 20px;
  }
`;

const InfoItem = styled.div`
  padding: 0 14px;
  line-height: 38px;
  display: flex;
  > span:first-child {
    width: 28%;
    display: inline-block;
  }
`;

const GridBox = styled.div`
  border: 1px solid var(--fett-border-default);
  margin-left: 12px;
  > div {
    line-height: 32px;
    display: flex;
    align-items: center;
  }
  > div:first-child {
    border-bottom: 1px solid var(--fett-border-default);
    line-height: 40px;
  }
  > div > span {
    padding: 0 8px 0 16px;
    display: inline-block;
    width: 50%;
  }
  > div > span:not(:first-child) {
    padding-left: 24px;
  }
  > div > span span {
    line-height: 18px;
  }
`;

export const BuyerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  span:not(:last-child) {
    margin-right: 8px;
  }
`;

export const EllipsisStyle = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const DetailDrawer = ({
  detail,
  visDrawer,
  setVisDrawer,
  setVisModalBuyer,
  setRefreshAgain,
}: {
  detail: IPhoneListRsp['data'][0];
  visDrawer: boolean;
  setVisDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  setVisModalBuyer: React.Dispatch<React.SetStateAction<boolean>>;
  setRefreshAgain: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t } = useTranslate();

  if (!detail) {
    return null;
  }

  const lastTime = Number(detail.lastChatTime);

  return (
    <Drawer
      title={t('WhatsappBuyers.WA买家详情')}
      placement="right"
      onClose={() => {
        setVisDrawer(false);
      }}
      visible={visDrawer}
      mask={false}
      width={560}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <div style={{ paddingBottom: '80px' }}>
          <TitleRow>{t('feedback.基本信息')}</TitleRow>
          <InfoItem>
            <span>{t('WhatsApp.table.buyerMobile')}</span>
            <span>
              {detail.phone
                ? `${detail.phone.slice(0, 2)}${'*'.repeat(
                    detail.phone.length - 4 > 0 ? detail.phone.length - 4 : 0,
                  )}${detail.phone.slice(detail.phone.length - 2)}`
                : ''}
            </span>
          </InfoItem>
          <InfoItem>
            <span>{t('WhatsappBuyers.地区')}</span>
            <span>{detail.region}</span>
          </InfoItem>
          <InfoItem>
            <span>{t('trade.买家备注')}</span>
            <span>
              <InlineEditor
                value={detail.remark}
                maxLength={64}
                onSubmit={(value) => {
                  return waBuyerComment({ phone: detail.phone, remark: value })
                    .then((resp) => {
                      message.success(t('translation.设置成功'));
                      setRefreshAgain((num) => num + 1);
                      return resp;
                    })
                    .catch(() => {
                      message.error(t('translation.操作失败，请稍后再试'));
                      return false;
                    });
                }}
              />
            </span>
          </InfoItem>

          <TitleRow>{t('WhatsappBuyers.关联平台信息')}</TitleRow>
          <GridBox>
            <div>
              <span>{t('marketing.店铺')}</span>
              <span>{t('remind.买家昵称')}</span>
            </div>
            {detail.buyers?.map(({ storeName, platform, region, buyerNick: buyerNickRaw }) => {
              const buyerNick = buyerNickMask(buyerNickRaw);
              return (
                <div>
                  <span>
                    <BuyerRow>
                      {platform === 'SHOPEE' ? (
                        <RedTag>{platform}</RedTag>
                      ) : (
                        <BlueTag>{platform}</BlueTag>
                      )}
                      <CountryFlagIcon region={region} />
                      <span title={storeName} className={EllipsisStyle}>
                        {storeName}
                      </span>
                    </BuyerRow>
                  </span>
                  <span title={buyerNick} className={EllipsisStyle}>
                    {buyerNick}
                  </span>
                </div>
              );
            })}
          </GridBox>

          <TitleRow>{t('WhatsappBuyers.历史联系')}</TitleRow>
          <GridBox>
            <div>
              <span>{t('WhatsappBuyers.WA帐号')}</span>
              <span>{t('WhatsappBuyers.发起时间')}</span>
            </div>
            <div>
              <span title={detail.lastWaba} className={EllipsisStyle}>
                {detail.lastWaba}
              </span>
              <span
                title={lastTime > 0 ? dayjs(lastTime).format('YYYY-MM-DD HH:mm:ss') : ''}
                className={EllipsisStyle}
              >
                {lastTime > 0 ? dayjs(lastTime).format('YYYY-MM-DD HH:mm:ss') : ''}
              </span>
            </div>
          </GridBox>
        </div>
        <div
          style={{
            border: '0',
            borderTop: '1px solid var(--fett-border-default)',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '64px',
            lineHeight: '64px',
            paddingLeft: '24px',
            background: 'var(--fett-bg-panel)',
          }}
        >
          <Button
            style={{ marginRight: '16px' }}
            onClick={(e) => {
              setVisModalBuyer(true);
            }}
          >
            {t('WhatsappBuyers.关联平台')}
          </Button>
          <WaConcatBtn phone={detail!.phone} hasIcon />
        </div>
      </div>
    </Drawer>
  );
};

export default DetailDrawer;
